import * as React from 'react';

import {
  useAssignTag,
  useCreateTag,
  useDeleteTag,
  useKbTagsQuery,
  useKbThreadDetailQuery,
  useUnassignTag,
} from '@/api/use-kb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// =============================================================================
// kb-tag-panel.tsx — Panneau TAXONOMIE / TAGGING de la KB (ADMIN uniquement, R15-COM-04).
// =============================================================================
// §2.7.4 : le tagging est une fonction d'aide à la décision INTERNE JeKo (ADMIN-ONLY ; §3.9 : aucun tag
// côté fournisseur). Ce panneau permet de : (1) LISTER la taxonomie, (2) CRÉER un tag (label + parent
// optionnel), (3) ASSIGNER / RETIRER un tag sur le fil sélectionné, (4) SUPPRIMER un tag de la taxonomie.
// Les mutations INVALIDENT les queries concernées (le contenu est re-lu par REST). WCAG : <label> associés,
// boutons nommés, tags en LIBELLÉ TEXTE. Rendu uniquement quand un fil est sélectionné (threadId non null).

export interface KbTagPanelProps {
  readonly threadId: string;
}

export function KbTagPanel({ threadId }: KbTagPanelProps): React.JSX.Element {
  const tagsQuery = useKbTagsQuery();
  const detailQuery = useKbThreadDetailQuery('admin', threadId);
  const createTag = useCreateTag();
  const deleteTag = useDeleteTag();
  const assignTag = useAssignTag(threadId);
  const unassignTag = useUnassignTag(threadId);

  const [label, setLabel] = React.useState('');
  const [parentTagId, setParentTagId] = React.useState('');
  const [assignChoice, setAssignChoice] = React.useState('');

  const allTags = tagsQuery.data?.tags ?? [];
  const assignedTags = detailQuery.data?.tags ?? [];
  const assignedIds = new Set(assignedTags.map((t) => t.tagId));
  const assignableTags = allTags.filter((t) => !assignedIds.has(t.tagId));

  const onCreate = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmed = label.trim();
    if (trimmed.length === 0 || createTag.isPending) {
      return;
    }
    const parent = parentTagId.trim();
    createTag.mutate(
      parent.length > 0 ? { label: trimmed, parentTagId: parent } : { label: trimmed },
      {
        onSuccess: () => {
          setLabel('');
          setParentTagId('');
        },
      },
    );
  };

  const onAssign = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (assignChoice.length === 0 || assignTag.isPending) {
      return;
    }
    assignTag.mutate(assignChoice, {
      onSuccess: () => setAssignChoice(''),
    });
  };

  return (
    <section
      aria-label="Tags et taxonomie"
      className="flex flex-col gap-4 border-t border-border p-4"
    >
      <h3 className="font-serif text-sm font-semibold">Tags du fil</h3>

      {/* Tags ASSIGNÉS au fil (retrait possible). */}
      {assignedTags.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {assignedTags.map((tag) => (
            <li key={tag.tagId}>
              <span className="inline-flex items-center gap-1">
                <Badge variant="outline">{tag.label}</Badge>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => unassignTag.mutate(tag.tagId)}
                  disabled={unassignTag.isPending}
                  aria-label={`Retirer le tag ${tag.label}`}
                >
                  Retirer
                </Button>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">
          Aucun tag assigné à ce fil.
        </p>
      )}

      {/* ASSIGNER un tag existant. */}
      <form onSubmit={onAssign} className="flex items-end gap-2">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="kb-assign-tag">Assigner un tag</Label>
          <select
            id="kb-assign-tag"
            value={assignChoice}
            onChange={(event) => setAssignChoice(event.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="">Choisir un tag…</option>
            {assignableTags.map((tag) => (
              <option key={tag.tagId} value={tag.tagId}>
                {tag.label}
              </option>
            ))}
          </select>
        </div>
        <Button
          type="submit"
          variant="secondary"
          disabled={assignChoice.length === 0 || assignTag.isPending}
        >
          Assigner
        </Button>
      </form>
      {assignTag.isError ? (
        <p role="alert" className="text-xs text-destructive">
          {assignTag.error?.message ?? "L'assignation a échoué."}
        </p>
      ) : null}

      {/* Taxonomie : créer un tag. */}
      <div className="border-t border-border pt-4">
        <h3 className="font-serif text-sm font-semibold">Taxonomie</h3>
        <form onSubmit={onCreate} className="mt-2 flex flex-col gap-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kb-new-tag-label">Nouveau tag</Label>
            <Input
              id="kb-new-tag-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Libellé du tag"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kb-new-tag-parent">Tag parent (optionnel)</Label>
            <select
              id="kb-new-tag-parent"
              value={parentTagId}
              onChange={(event) => setParentTagId(event.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Aucun (tag racine)</option>
              {allTags.map((tag) => (
                <option key={tag.tagId} value={tag.tagId}>
                  {tag.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-end gap-3">
            {createTag.isError ? (
              <p role="alert" className="text-xs text-destructive">
                {createTag.error?.message ?? 'La création a échoué.'}
              </p>
            ) : null}
            <Button
              type="submit"
              disabled={label.trim().length === 0 || createTag.isPending}
            >
              {createTag.isPending ? 'Création…' : 'Créer le tag'}
            </Button>
          </div>
        </form>

        {/* Liste de la taxonomie existante (suppression possible). */}
        {allTags.length > 0 ? (
          <ul className="mt-3 flex flex-col divide-y divide-border">
            {allTags.map((tag) => (
              <li
                key={tag.tagId}
                className="flex items-center justify-between gap-2 py-2 text-sm"
              >
                <span className="min-w-0 truncate">
                  {tag.label}
                  {tag.parentTagId !== null ? (
                    <span className="ml-1 text-xs text-muted-foreground">
                      (sous-tag)
                    </span>
                  ) : null}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteTag.mutate(tag.tagId)}
                  disabled={deleteTag.isPending}
                  aria-label={`Supprimer le tag ${tag.label} de la taxonomie`}
                >
                  Supprimer
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground">
            Aucun tag dans la taxonomie.
          </p>
        )}
        {deleteTag.isError ? (
          <p role="alert" className="mt-2 text-xs text-destructive">
            {deleteTag.error?.message ?? 'La suppression a échoué.'}
          </p>
        ) : null}
      </div>
    </section>
  );
}
